/**
 * StoreHydration
 *
 * Mount-only client component. Calls store.hydrate() exactly once on the
 * first client render so any persisted state in localStorage replaces the
 * SSR-built initial state. Renders nothing.
 *
 * The store ships with hydrated=false; until this fires, the persistence
 * write-through subscription is a no-op (it short-circuits on !hydrated),
 * which prevents the SSR snapshot from clobbering the user's saved state.
 *
 * Stage FW-2a: After localStorage hydration, also hydrates Stations + Hotels
 * from backend (replacing mock-JSON seed for these two). Crew stays on mock
 * (PII handling unresolved). Error handling is done at the page level using
 * components/ui/error-state.tsx — this component just triggers the fetch.
 *
 * TIMING FIX: Masters fetch must wait until user is authenticated (tokens are
 * in-memory, not persisted). We subscribe to accessToken and trigger the fetch
 * when it becomes truthy (post-login).
 *
 * LOGOUT/RE-LOGIN FIX: the guard is keyed on the authenticated user's id
 * (falling back to a fixed sentinel while no user is present yet), not a
 * one-shot boolean. Logout is an SPA navigation — this component stays
 * mounted — so a plain "already fetched" boolean would never reset and
 * hotels/stations would never re-hydrate on re-login in the same tab.
 * Keying on user id, and clearing the guard whenever accessToken drops out,
 * covers both re-login of the same account and switching between two
 * different accounts in the same tab without a full logout in between.
 */

import { useEffect, useRef } from 'react';
import { useAppStore } from '@/lib/store';
import { useAuthStore } from '@/lib/store/auth-slice';
import { hydrateBranding, hydrateMastersFromBackend } from '@/lib/store/hydrate-masters';

export function StoreHydration() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const hydratedForUserRef = useRef<string | null>(null);

  // 1. Hydrate from localStorage ONCE at mount
  useEffect(() => {
    useAppStore.getState().hydrate();
  }, []);

  // 2. Hydrate stations + hotels from backend when the authenticated user
  // changes (separate effect so localStorage hydration runs immediately at
  // mount, backend fetch waits for token to be set post-login).
  useEffect(() => {
    if (!accessToken) {
      hydratedForUserRef.current = null; // logged out — clear the guard so the next login re-fetches
      // No airline identity or tenant config outlives the session.
      useAppStore.setState({ branding: null, systemConfig: null });
      return;
    }
    const guardKey = userId ?? '__anonymous__';
    if (hydratedForUserRef.current !== guardKey) {
      hydratedForUserRef.current = guardKey;
      void hydrateBranding();
      hydrateMastersFromBackend().catch((err) => {
        console.error('[StoreHydration] Failed to hydrate masters from backend:', err);
        // Pages will continue to use mock seed on error (graceful degradation)
      });
    }
  }, [accessToken, userId]);

  return null;
}
